import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAnalytics, logEvent, type Analytics } from 'firebase/analytics';
import { captureException } from './sentry';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

let analytics: Analytics | null = null;

// Initialize Firebase only if config is available
export function initFirebase(): FirebaseApp | null {
  if (!firebaseConfig.apiKey) {
    return null;
  }

  try {
    const app = initializeApp(firebaseConfig);
    analytics = getAnalytics(app);
    return app;
  } catch (error) {
    captureException(error, { lib: 'firebase', action: 'initFirebase' });
    return null;
  }
}

// Track custom events (non-blocking, but still observable)
export function trackEvent(eventName: string, params?: Record<string, unknown>) {
  if (!analytics) {
    return;
  }

  try {
    logEvent(analytics, eventName, params);
  } catch (error) {
    captureException(error, {
      lib: 'firebase',
      action: 'trackEvent',
      eventName,
      severity: 'low',
    });
  }
}

// Pre-defined event helpers
export const events = {
  pageView: (pageTitle: string, language: string) => {
    trackEvent('page_view', { page_title: pageTitle, language });
  },

  languageToggle: (fromLang: string, toLang: string) => {
    trackEvent('language_toggle', { from_language: fromLang, to_language: toLang });
  },

  communityCardClick: (communityId: string, status: string) => {
    trackEvent('community_card_click', { community_id: communityId, status });
  },

  ctaClick: (buttonName: string, section: string) => {
    trackEvent('cta_click', { button_name: buttonName, section });
  },

  waitlistClick: (communityId: string) => {
    trackEvent('waitlist_click', { community_id: communityId });
  },

  appStoreClick: (store: 'google_play' | 'app_store') => {
    trackEvent('app_store_click', { store });
  },
};
